// src/components/listing/ListingDetails.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, FiEye, FiImage, FiCalendar, FiTag, 
  FiBriefcase, FiTool, FiDollarSign, FiMapPin, 
  FiEdit, FiTrendingUp, FiBarChart2, FiCheckCircle,
  FiRewind, FiTrash2, FiStar, FiMessageSquare,
  FiShoppingBag, FiExternalLink
} from 'react-icons/fi';
import Badge from '../../../components/common/Badge';
import Button from '../../../components/common/Button';
import Loading from '../../../components/common/Loading';
import ReviewItem from '../../../components/listing/ReviewItem';
import BoostStatus from '../../../components/store/boost/BoostStatus';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import { useAuth } from '../../../context/AuthContext';
import { useSellerData } from '../../../hooks/useSellerData';
import { useListingActions } from '../../../hooks/useListingActions';
import { getListingRatings } from '../../../services/api';
import { getImageUrl } from '../../../utils/imageUtils';

const ListingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { listings, orders, loading, fetchData } = useSellerData();
  const listing = listings?.find(l => l.listing_id?.toString() === id);
  
  // Initialize listing actions with fetchData callback to refresh data
  const listingActions = useListingActions(fetchData);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch reviews
  useEffect(() => {
    const fetchReviews = async () => {
      if (!listing) return;
      
      setReviewsLoading(true);
      try {
        const reviewsResponse = await getListingRatings(listing.listing_id);
        if (reviewsResponse.success) {
          setReviews(reviewsResponse.data);
        }
      } catch (error) {
        // console.error('Error fetching reviews:', error);
      } finally {
        setReviewsLoading(false);
      }
    };
    
    if (listing) {
      fetchReviews();
    }
  }, [listing]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="large" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
            <FiImage className="w-12 h-12 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Listing Not Found
          </h2>
          <p className="text-gray-600 mb-8">
            We couldn't find the listing you're looking for. It may have been removed or you don't have access to view it.
          </p>
          <Button 
            onClick={() => navigate(-1)}
            variant="primary"
            size="large"
            className="px-8"
          >
            Go Back to Listings
          </Button>
        </div>
      </div>
    );
  }

  // Helper functions
  const listingOrders = orders?.filter((o) => o.listing_id === listing.listing_id) || [];
  const photosCount = listing.images ? listing.images.length : 0;
  const viewCount = listing.view_count || 0;
  
  const listedDate = new Date(listing.created_at).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const primaryImageUrl = getImageUrl(listing.primary_image?.image_url || listing.images?.[0]?.image_url);

  // Handlers using useListingActions
  const handleEditListing = () => {
    listingActions.handleEditListing(listing.listing_id);
  };

  const handleMarkAsSold = () => {
    listingActions.handleMarkAsSold(listing.listing_id);
    setSuccessMessage('Listing marked as sold successfully! ✅');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleReactivate = () => {
    listingActions.handleReactivate(listing.listing_id);
    setSuccessMessage('Listing reactivated successfully! 🔄');
  };

  const handleDelete = () => {
    listingActions.handleDeleteListing(listing.listing_id, listing.title);
    setSuccessMessage('Listing deleted successfully! 🗑️');
    // Navigate back after delete
    setTimeout(() => {
      navigate('/store/listings');
    }, 1500);
  };

  const handleBoost = () => {
    navigate(`/store/boost-package/${listing.listing_id}`);
  };

  const handleViewPerformance = () => {
    navigate(`/store/listings/performance/${listing.listing_id}`);
  };

  const handleViewOrders = () => {
    navigate('/store/orders');
  };

  // Get average rating
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + (r.rating_overall || 0), 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success Message Toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <FiCheckCircle className="w-5 h-5" />
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <Header />
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <FiArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-xl font-bold text-gray-900 truncate max-w-md">
                {listing.title}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image and Basic Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="relative">
                <img
                  src={primaryImageUrl}
                  alt={listing.title}
                  className="w-full h-80 object-cover"
                  onError={(e) => {
                    e.target.src = '/images/placeholder.jpg';
                  }}
                />
                <div className="absolute top-4 left-4 flex gap-2">
                  <Badge type="status" status={listing.status} />
                  {listing.is_negotiable && (
                    <div className="flex items-center gap-1 bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      <FiTool className="w-3 h-3" />
                      <span>Negotiable</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Title and Stats */}
              <div className="p-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-3">
                  {listing.title}
                </h1>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-3xl font-bold text-orange-600">
                    ₦{parseFloat(listing.price || 0).toLocaleString()}
                  </span>
                </div>
                
                <div className="flex items-center gap-6 text-gray-600">
                  <div className="flex items-center gap-2">
                    <FiEye className="w-4 h-4" />
                    <span className="text-sm">{viewCount} views</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FiImage className="w-4 h-4" />
                    <span className="text-sm">{photosCount} photos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FiCalendar className="w-4 h-4" />
                    <span className="text-sm">Listed {listedDate}</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="border-t border-gray-100 p-4">
                <div className="flex gap-4">
                  <button
                    onClick={handleViewPerformance}
                    className="flex-1 flex flex-col items-center p-3 hover:bg-gray-50 rounded-lg transition"
                  >
                    <FiBarChart2 className="w-5 h-5 text-orange-600 mb-1" />
                    <span className="text-xs font-medium text-gray-700">Performance</span>
                  </button>
                  <button
                    onClick={handleBoost}
                    className="flex-1 flex flex-col items-center p-3 hover:bg-gray-50 rounded-lg transition"
                  >
                    <FiTrendingUp className="w-5 h-5 text-orange-600 mb-1" />
                    <span className="text-xs font-medium text-gray-700">Boost</span>
                  </button>
                  <button
                    onClick={handleEditListing}
                    className="flex-1 flex flex-col items-center p-3 hover:bg-gray-50 rounded-lg transition"
                  >
                    <FiEdit className="w-5 h-5 text-orange-600 mb-1" />
                    <span className="text-xs font-medium text-gray-700">Edit</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Boost Status */}
            {(listing.status === 'active' || listing.status === 'draft') && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Boost Status</h2>
                <BoostStatus 
                  listingId={listing.listing_id}
                  showManageButton={true}
                  showPerformance={true}
                  onViewPerformance={handleViewPerformance}
                  onBoost={handleBoost}
                />
              </div>
            )}

            {/* Listing Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Listing Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FiTag className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Price</p>
                      <p className="font-semibold text-gray-900">
                        ₦{parseFloat(listing.price || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FiBriefcase className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Category</p>
                      <p className="font-semibold text-gray-900">{listing.category_name}</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FiTool className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Condition</p>
                      <div className="mt-1">
                        <Badge type="condition" condition={listing.condition} />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FiDollarSign className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Negotiable</p>
                      <p className="font-semibold text-gray-900">
                        {listing.is_negotiable ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex items-start gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FiMapPin className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-semibold text-gray-900">
                    {listing.lcda_name}, {listing.lga_name}
                    {listing.state_name && `, ${listing.state_name}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-line">
                  {listing.description || 'No description provided for this listing.'}
                </p>
              </div>
            </div>

            {/* Orders Section */}
            {listingOrders.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Order History ({listingOrders.length})
                  </h2>
                  <button
                    onClick={handleViewOrders}
                    className="text-sm font-medium text-orange-600 hover:text-orange-700 flex items-center gap-1"
                  >
                    View All
                    <FiExternalLink className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  {listingOrders.slice(0, 3).map((order) => (
                    <div 
                      key={order.order_id} 
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition cursor-pointer"
                      onClick={() => navigate(`/store/orders/order-details/${order.order_id}`)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">
                          Order #{order.order_id}
                        </span>
                        <Badge type="orderStatus" status={order.status} />
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          {order.buyer_first_name} {order.buyer_last_name}
                        </span>
                        <span className="font-semibold text-orange-600">
                          ₦{parseFloat(order.total_amount || 0).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        {new Date(order.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Moderation Status */}
            {listing.moderation_status !== 'approved' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Moderation Status</h2>
                <div className="space-y-4">
                  <Badge type="moderation" status={listing.moderation_status} />
                  {listing.admin_notes && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-orange-800 mb-2">Admin Notes:</p>
                      <p className="text-orange-900">{listing.admin_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Reviews Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Reviews ({reviews.length})
                </h2>
                {reviews.length > 3 && (
                  <button
                    onClick={() => setShowAllReviews(!showAllReviews)}
                    className="text-sm font-medium text-orange-600 hover:text-orange-700"
                  >
                    {showAllReviews ? 'Show Less' : 'View All'}
                  </button>
                )}
              </div>
              
              {reviewsLoading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                  <p className="mt-3 text-gray-600">Loading reviews...</p>
                </div>
              ) : reviews.length > 0 ? (
                <>
                  <div className="space-y-6">
                    {(showAllReviews ? reviews : reviews.slice(0, 3)).map((review) => (
                      <ReviewItem key={review.rating_id || review.id} review={review} />
                    ))}
                  </div>
                  
                  {reviews.length > 0 && !showAllReviews && (
                    <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <FiStar className="w-4 h-4 text-yellow-400" />
                          <span className="font-medium">{averageRating}/5</span>
                        </div>
                        <span>•</span>
                        <span>{reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <FiStar className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
                  <p className="text-gray-600">
                    Customers will see reviews here after purchasing
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Actions and Summary */}
          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Listing Actions</h2>
              
              <div className="space-y-3">
                {/* Status Management Buttons */}
                {listing.status === 'active' && (
                  <Button
                    onClick={handleMarkAsSold}
                    variant="success"
                    fullWidth
                    icon={<FiCheckCircle className="w-5 h-5" />}
                    iconPosition="left"
                  >
                    Mark as Sold
                  </Button>
                )}
                
                {listing.status === 'sold' && (
                  <Button
                    onClick={handleReactivate}
                    variant="warning"
                    fullWidth
                    icon={<FiRewind className="w-5 h-5" />}
                    iconPosition="left"
                  >
                    Reactivate Listing
                  </Button>
                )}

                {/* Delete Button */}
                <Button
                  onClick={handleDelete}
                  variant="danger"
                  fullWidth
                  icon={<FiTrash2 className="w-5 h-5" />}
                  iconPosition="left"
                >
                  Delete Listing
                </Button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Views</span>
                  <span className="font-semibold text-gray-900">{viewCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Photos</span>
                  <span className="font-semibold text-gray-900">{photosCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Orders</span>
                  <span className="font-semibold text-gray-900">{listingOrders.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Rating</span>
                  <span className="font-semibold text-gray-900">{averageRating}/5</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  <Badge type="status" status={listing.status} />
                </div>
              </div>
            </div>

            {/* Additional Images (if any) */}
            {listing.images && listing.images.length > 1 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">More Photos</h2>
                <div className="grid grid-cols-2 gap-3">
                  {listing.images.slice(1, 5).map((image, index) => (
                    <div key={index} className="aspect-square rounded-lg overflow-hidden">
                      <img
                        src={getImageUrl(image.image_url)}
                        alt={`${listing.title} - ${index + 2}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                      />
                    </div>
                  ))}
                </div>
                {listing.images.length > 5 && (
                  <p className="text-sm text-gray-500 mt-3 text-center">
                    +{listing.images.length - 5} more photos
                  </p>
                )}
              </div>
            )}

            {/* Listing ID and Metadata */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Listing Details</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Listing ID</span>
                  <span className="font-medium text-gray-900">{listing.listing_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Created</span>
                  <span className="font-medium text-gray-900">{listedDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated</span>
                  <span className="font-medium text-gray-900">
                    {new Date(listing.updated_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Use the hook's confirmation modal */}
      <listingActions.ConfirmationModalComponent />
      
      <Footer />
    </div>
  );
};

export default ListingDetails;